import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';


// Create axios instance with interceptors
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Get tenant headers from TenantProvider stored in sessionStorage.
 * This is a bridge so the axios interceptor can add headers without
 * requiring React context (interceptors run outside component tree).
 */
function getStoredTenantHeaders() {
  const headers = {};
  const mode = sessionStorage.getItem('tenant_mode');
  const slug = sessionStorage.getItem('tenant_slug');
  if (mode === 'platform') {
    headers['X-Tenant-Mode'] = 'platform';
  } else if (slug) {
    headers['X-Tenant-Slug'] = slug;
  }
  return headers;
}

// Request interceptor: add tenant headers to every API call
api.interceptors.request.use((config) => {
  const tenantHeaders = getStoredTenantHeaders();
  Object.assign(config.headers, tenantHeaders);

  // P1 ASSIST MODE READ-ONLY GUARD:
  // Blocks tenant write actions in browser when Platform Admin enters read-only assist mode.
  // Platform auth endpoints and assist exit audit remain allowed.
  const isAssistMode = localStorage.getItem('assist_mode') === 'true';
  const assistAccessMode = localStorage.getItem('assist_access_mode');
  const method = String(config.method || 'get').toLowerCase();
  const url = String(config.url || '');

  const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
  const allowlisted =
    url.includes('/platform/assist/exit') ||
    url.includes('/platform/auth/') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout');

  if (isAssistMode && assistAccessMode === 'read_only' && isWrite && !allowlisted) {
    return Promise.reject({
      response: {
        status: 403,
        data: {
          detail: 'Read-only Assist Mode is active. Write actions are blocked.'
        }
      },
      message: 'Read-only Assist Mode is active. Write actions are blocked.'
    });
  }

  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token on mount
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  /**
   * Store tenant context so the API interceptor can add headers.
   * Called by the app when TenantProvider resolves.
   */
  const setTenantContext = useCallback((mode, slug) => {
    if (mode) sessionStorage.setItem('tenant_mode', mode);
    if (slug) sessionStorage.setItem('tenant_slug', slug);
  }, []);

  const login = async (email, password) => {
  try {
    const tenantSlug = window.location.hostname.split('.')[0];

    const response = await api.post('/auth/login', {
      email,
      password,
      tenant_slug: tenantSlug
    });

    const { access_token, refresh_token, user: userData } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));

    // Keep the hostname/TenantProvider tenant context when the login
    // response has no tenant_slug. Writing undefined here caused the
    // first authenticated request to use an invalid tenant header.
    const resolvedTenantSlug = (
      userData?.tenant_slug
      || sessionStorage.getItem('tenant_slug')
      || tenantSlug
    );

    if (resolvedTenantSlug) {
      sessionStorage.setItem('tenant_slug', resolvedTenantSlug);
    }

    if (!sessionStorage.getItem('tenant_mode')) {
      sessionStorage.setItem('tenant_mode', 'tenant');
    }

    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);

    return { success: true, user: userData };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.detail || 'Login failed'
    };
  }
};

  /**
   * Platform admin login — uses separate /api/platform/auth/login endpoint.
   */
  const platformLogin = async (email, password) => {
    try {
      const response = await api.post('/platform/auth/login', { email, password });
      const { access_token, refresh_token, user: userData, must_change_password } = response.data;
      // Critical fix
      sessionStorage.setItem('tenant_slug', userData.tenant_slug);

      // Persist must_change_password in the user object so it survives refreshes
      const enrichedUser = { ...userData, must_change_password: !!must_change_password };

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(enrichedUser));

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(enrichedUser);

      return { success: true, must_change_password: !!must_change_password };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Platform login failed',
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('tenant_mode');
    sessionStorage.removeItem('tenant_slug');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  /**
   * Update the stored user object (e.g. to clear must_change_password after reset).
   */
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'operations_admin';
  const isSupervisor = isAdmin || user?.role === 'supervisor';
  const isOfficer = isSupervisor || user?.role === 'security_officer';
  const isPlatformAdmin = user?.platform_role === 'platform_admin' || user?.role === 'platform_admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      login,
      platformLogin,
      logout, 
      loading, 
      isAdmin, 
      isSupervisor, 
      isOfficer,
      isPlatformAdmin,
      updateUser,
      setTenantContext,
      api 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

let refreshPromise = null;

// Add auth interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const isOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;

    // 1) One silent retry for true network/no-response failures
    if (!error.response && !originalRequest._networkRetried) {
      originalRequest._networkRetried = true;

      try {
        return await api.request(originalRequest);
      } catch (retryError) {
        const retryIsOffline =
          typeof navigator !== "undefined" && navigator.onLine === false;

        return Promise.reject({
          error: retryIsOffline ? "Offline" : "Connection Error",
          message: retryIsOffline
            ? "You are offline. Please check your internet connection."
            : "The server could not be reached. Please try again.",
          offline: retryIsOffline,
          originalError: retryError.message,
        });
      }
    }

    // 2) Renew expired access tokens once, preserving tenant isolation.
    const requestUrl = String(originalRequest.url || '');
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/logout');

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      const storedRefreshToken = localStorage.getItem('refresh_token');
      const storedUser = localStorage.getItem('user');
      const currentAccessToken = localStorage.getItem('access_token');

      if (!storedRefreshToken || !storedUser) {
        return Promise.reject(error);
      }

      // Another request may already have renewed the expired token.
      const failedAuthorization =
        originalRequest.headers?.Authorization ||
        originalRequest.headers?.authorization ||
        '';

      if (
        currentAccessToken &&
        failedAuthorization &&
        failedAuthorization !== `Bearer ${currentAccessToken}`
      ) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] =
          `Bearer ${currentAccessToken}`;
        return api.request(originalRequest);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const parsedUser = JSON.parse(storedUser);
            const isPlatform =
              parsedUser?.platform_role === 'platform_admin' ||
              parsedUser?.role === 'platform_admin';

            const response = isPlatform
              ? await axios.post(
                  '/api/platform/auth/refresh',
                  null,
                  {
                    headers: {
                      Authorization: `Bearer ${storedRefreshToken}`,
                      'X-Tenant-Mode': 'platform',
                    },
                    withCredentials: true,
                  }
                )
              : await axios.post(
                  '/api/auth/refresh',
                  null,
                  {
                    params: { refresh_token: storedRefreshToken },
                    headers: getStoredTenantHeaders(),
                    withCredentials: true,
                  }
                );

            const renewedToken = response.data?.access_token;

            if (!renewedToken) {
              throw new Error('Refresh response contained no access token');
            }

            // Do not restore a session that was replaced during refresh.
            if (
              localStorage.getItem('refresh_token') !== storedRefreshToken
            ) {
              throw new Error('Session changed during token renewal');
            }

            localStorage.setItem('access_token', renewedToken);
            api.defaults.headers.common['Authorization'] =
              `Bearer ${renewedToken}`;

            return renewedToken;
          })().finally(() => {
            refreshPromise = null;
          });
        }

        const renewedToken = await refreshPromise;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] =
          `Bearer ${renewedToken}`;

        return api.request(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // 3) Final fallback for unrecoverable no-response errors
    if (!error.response) {
      return Promise.reject({
        error: isOffline ? "Offline" : "Connection Error",
        message: isOffline
          ? "You are offline. Please check your internet connection."
          : "The server could not be reached. Please try again.",
        offline: isOffline,
        originalError: error.message,
      });
    }

    // 4) Normal API errors
    return Promise.reject(error);
  }
);

export { api };
export default api;

